import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Brain, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

interface PsychotherapySessionCardProps {
  records: any[];
  onRecordsChange: (updatedRecords: any[]) => void;
}

export function PsychotherapySessionCard({ records, onRecordsChange }: PsychotherapySessionCardProps) {
  const therapyRecords = records.filter(r => r.type === 'therapy_note');
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<any>({});

  if (therapyRecords.length === 0) return null;

  const handleEdit = (idx: number) => {
    const originalIndex = records.indexOf(therapyRecords[idx]);
    setEditForm(records[originalIndex]);
    setEditingIndex(originalIndex);
  };

  const handleSaveEdit = () => {
    if (editingIndex === null) return;
    const updated = [...records];
    updated[editingIndex] = editForm;
    onRecordsChange(updated);
    setEditingIndex(null);
    toast.success("Registro atualizado!");
  };

  const handleDelete = (idx: number) => {
    const originalIndex = records.indexOf(therapyRecords[idx]);
    setDeleteIndex(originalIndex);
  };

  const confirmDelete = () => {
    if (deleteIndex === null) return;
    const updated = records.filter((_, i) => i !== deleteIndex);
    onRecordsChange(updated);
    setDeleteIndex(null);
    toast.success("Registro excluído!");
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Brain className="h-5 w-5 text-pink-600" />
        <h3 className="text-lg font-semibold text-pink-600">Sessão de Psicoterapia</h3>
        <Badge variant="secondary">{therapyRecords.length} registros</Badge>
      </div>

      <div className="space-y-3">
        {therapyRecords.map((record, idx) => (
          <Card key={idx} className="border-l-4 border-l-pink-600">
            <CardContent className="pt-4">
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <Badge variant="outline" className="capitalize">
                    {record.session_type}
                  </Badge>
                  <div className="flex items-center gap-2">
                    {record.date && (
                      <span className="text-xs text-muted-foreground">
                        {new Date(record.date).toLocaleDateString('pt-BR')}
                      </span>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleEdit(idx)}
                      className="text-primary hover:bg-primary/10"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDelete(idx)}
                      className="text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {record.chief_complaint && (
                  <div>
                    <p className="text-xs font-medium text-muted-foreground">Queixa Principal</p>
                    <p className="text-sm text-foreground">{record.chief_complaint}</p>
                  </div>
                )}

                {record.intervention && (
                  <div>
                    <p className="text-xs font-medium text-muted-foreground">Intervenção</p>
                    <p className="text-sm text-foreground">{record.intervention}</p>
                  </div>
                )}

                {record.technique && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">Técnica:</span>
                    <Badge variant="secondary" className="text-xs">{record.technique}</Badge>
                  </div>
                )}

                {record.patient_response && (
                  <div className="bg-pink-50 border border-pink-200 rounded p-2">
                    <p className="text-xs text-pink-700">
                      <span className="font-medium">Resposta:</span> {record.patient_response}
                    </p>
                  </div>
                )}

                {record.homework && (
                  <div className="bg-amber-50 border border-amber-200 rounded p-2">
                    <p className="text-xs text-amber-700">
                      📝 <span className="font-medium">Tarefa:</span> {record.homework}
                    </p>
                  </div>
                )}

                {record.next_plan && (
                  <div>
                    <p className="text-xs font-medium text-muted-foreground">Próxima Sessão</p>
                    <p className="text-sm text-foreground">{record.next_plan}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <AlertDialog open={editingIndex !== null} onOpenChange={() => setEditingIndex(null)}>
        <AlertDialogContent className="max-w-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Editar Sessão de Psicoterapia</AlertDialogTitle>
          </AlertDialogHeader>

          <div className="grid gap-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="grid gap-2">
              <Label>Tipo de Sessão</Label>
              <Input
                value={editForm.session_type || ""}
                onChange={(e) => setEditForm({ ...editForm, session_type: e.target.value })}
                placeholder="Ex: Individual, Grupo"
              />
            </div>

            <div className="grid gap-2">
              <Label>Data</Label>
              <Input
                type="date"
                value={editForm.date || ""}
                onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
              />
            </div>

            <div className="grid gap-2">
              <Label>Queixa Principal</Label>
              <Textarea
                value={editForm.chief_complaint || ""}
                onChange={(e) => setEditForm({ ...editForm, chief_complaint: e.target.value })}
              />
            </div>

            <div className="grid gap-2">
              <Label>Intervenção</Label>
              <Textarea
                value={editForm.intervention || ""}
                onChange={(e) => setEditForm({ ...editForm, intervention: e.target.value })}
              />
            </div>

            <div className="grid gap-2">
              <Label>Técnica Utilizada</Label>
              <Input
                value={editForm.technique || ""}
                onChange={(e) => setEditForm({ ...editForm, technique: e.target.value })}
                placeholder="Ex: TCC, Mindfulness"
              />
            </div>

            <div className="grid gap-2">
              <Label>Resposta do Paciente</Label>
              <Textarea
                value={editForm.patient_response || ""}
                onChange={(e) => setEditForm({ ...editForm, patient_response: e.target.value })}
              />
            </div>

            <div className="grid gap-2">
              <Label>Tarefa de Casa</Label>
              <Textarea
                value={editForm.homework || ""}
                onChange={(e) => setEditForm({ ...editForm, homework: e.target.value })}
              />
            </div>

            <div className="grid gap-2">
              <Label>Plano para Próxima Sessão</Label>
              <Textarea
                value={editForm.next_plan || ""}
                onChange={(e) => setEditForm({ ...editForm, next_plan: e.target.value })}
              />
            </div>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleSaveEdit}>Salvar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteIndex !== null} onOpenChange={() => setDeleteIndex(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este registro? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}